import React, { useRef, useState } from 'react';
import { AlertTriangleIcon, CheckCircle2Icon, Loader2Icon, PaperclipIcon } from 'lucide-react';
import { WHATSAPP_URL } from '../../data/content';
import { formatMadridTimestamp, sendLeadEmail } from '../../utils/leadEmail';
import { CalculatorResult, CalculatorState, FLOOR_OPTIONS, SKIRTING_OPTIONS } from './useCalculator';
interface LeadFormProps {
  state: CalculatorState;
  result: CalculatorResult;
}
type Status = 'idle' | 'sending' | 'sent' | 'error';
const TIMINGS = ['Lo antes posible', 'En 2–4 semanas', 'En 1–3 meses', 'Solo estoy informándome'];
const LEVELING_LABELS: Record<CalculatorState['leveling'], string> = {
  apto: 'Apta para instalar',
  autonivelar: 'Hay que autonivelar',
  'no-lo-se': 'No lo sabe, necesita asesoramiento'
};
const fieldClass = 'w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-[14px] text-ink placeholder:text-stone-400 transition-colors duration-200 ease-premium focus:border-walnut focus:outline-none focus:ring-1 focus:ring-walnut';
const labelClass = 'mb-2 block text-[11px] uppercase tracking-overline text-stone-600';
const euro = (value: number) => new Intl.NumberFormat('es-ES', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0
}).format(value);
export function LeadForm({
  state,
  result
}: LeadFormProps) {
  const [status, setStatus] = useState<Status>('idle');
  const [fileName, setFileName] = useState<string | null>(null);
  const filesRef = useRef<HTMLInputElement>(null);
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus('sending');
    const form = new FormData(event.currentTarget);
    const now = new Date();
    const floor = FLOOR_OPTIONS.find(f => f.id === state.floor);
    const skirting = SKIRTING_OPTIONS.find(s => s.id === state.skirting);
    try {
      await sendLeadEmail({
        subject: `Nueva solicitud Suelo Perfecto · ${String(form.get('name') ?? '')} · ${euro(result.min)}–${euro(result.max)}`,
        fields: {
          'Fecha y hora (Madrid)': formatMadridTimestamp(now),
          Nombre: String(form.get('name') ?? ''),
          Teléfono: String(form.get('phone') ?? ''),
          'Código postal': String(form.get('zip') ?? ''),
          'Metros indicados en el formulario': `${form.get('meters') ?? ''} m²`,
          'Cuándo quiere instalar': String(form.get('timing') ?? ''),
          Fotos: fileName ? fileName : 'No adjuntó fotos',
          '— Estimación de la calculadora —': '',
          'Tipo de suelo': `${floor?.label ?? '-'} (${floor?.price ?? '-'} €/m²)`,
          Rodapiés: `${skirting?.label ?? '-'} (${skirting?.price ?? '-'} €/ml)`,
          'Superficie calculada': `${state.meters} m²`,
          Servicio: state.service === 'completo' ? 'Servicio completo (obra)' : 'Solo material',
          Mobiliario: state.furniture === 'si' ? 'Hay muebles que mover' : 'Vivienda vacía',
          'Suelo antiguo': state.removal === 'si' ? 'Hay que retirarlo' : 'No hay que retirarlo',
          Nivelación: LEVELING_LABELS[state.leveling],
          'Puertas a ajustar': `${state.doors}`,
          'Desglose calculado': result.breakdown.map(item => `${item.label}: ${euro(item.amount)} (${item.detail})`).join(' | '),
          'Estimación (sin IVA)': `${euro(result.min)} – ${euro(result.max)}`,
          'Total calculado (sin IVA)': euro(result.total),
          'Requiere revisión técnica de nivelación': result.needsTechnicalReview ? 'Sí' : 'No'
        },
        files: filesRef.current?.files ?? null
      });
      setStatus('sent');
    } catch {
      setStatus('error');
    }
  };
  if (status === 'sent') {
    return <div className="flex flex-col items-start gap-3 rounded-3xl border border-ink/10 bg-sand p-8" data-id="element-119" data-mp="117ofmcsulr4">
        <CheckCircle2Icon className="h-6 w-6 text-walnut" aria-hidden="true" data-id="element-120" data-mp="27rnx7sj0gttk" />
        <h3 className="font-serif text-2xl font-light text-ink" data-id="element-121" data-mp="22t2y5wfhwg6p">Solicitud enviada</h3>
        <p className="text-[13.5px] leading-relaxed text-stone-600" data-id="element-122" data-mp="249zwt9jx4p9f">
          Hemos recibido tus datos junto con la estimación que has calculado. Revisamos tu caso y te
          contactamos con la orientación de material y los siguientes pasos para el presupuesto
          cerrado. Normalmente respondemos el mismo día laborable.
        </p>
      </div>;
  }
  return <form onSubmit={handleSubmit} className="rounded-3xl border border-ink/10 bg-cream p-8 lg:p-10" aria-label="Solicitar presupuesto cerrado" data-id="element-123" data-mp="1liz1cvi0bbum">
      <h3 className="font-serif text-2xl font-light text-ink" data-id="element-124" data-mp="1qfpgnglzm3ez">Quiero mi presupuesto cerrado</h3>
      <p className="mt-2.5 text-[13px] leading-relaxed text-stone-600" data-id="element-125" data-mp="1vflltj6f80w4">
        Déjanos los datos y revisamos tu caso antes de darte un precio definitivo. Recibiremos tu
        solicitud con la estimación completa que acabas de calcular.
      </p>

      <div className="mt-8 grid gap-5 sm:grid-cols-2" data-id="element-126" data-mp="17qsopje2bmzx">
        <div data-id="element-127" data-mp="17p7ope8zbbuo">
          <label className={labelClass} htmlFor="lead-name" data-id="element-128" data-mp="3l1lmtzjsgeaa">
            Nombre
          </label>
          <input id="lead-name" name="name" required className={fieldClass} placeholder="Tu nombre" data-id="element-129" data-mp="3h00f0sm42q6y" />
        </div>
        <div data-id="element-130" data-mp="3c307sedizhe9">
          <label className={labelClass} htmlFor="lead-phone" data-id="element-131" data-mp="371t16in4co96">
            Teléfono
          </label>
          <input id="lead-phone" name="phone" type="tel" required className={fieldClass} placeholder="+34 600 000 000" autoComplete="tel" data-id="element-132" data-mp="322pvcvzbee05" />
        </div>
        <div data-id="element-133" data-mp="2tb7eeiujnu60">
          <label className={labelClass} htmlFor="lead-zip" data-id="element-134" data-mp="2taxqr1wz1z3h">
            Código postal
          </label>
          <input id="lead-zip" name="zip" required className={fieldClass} placeholder="08010" data-id="element-135" data-mp="2od5xa3goi4x6" />
        </div>
        <div data-id="element-136" data-mp="2jecgo263hxss">
          <label className={labelClass} htmlFor="lead-meters" data-id="element-137" data-mp="2je2tsngck6rb">
            Metros aproximados
          </label>
          <input id="lead-meters" name="meters" type="number" min={1} defaultValue={state.meters} className={fieldClass} data-id="element-138" data-mp="24psykso1o4lh" />
        </div>
        <div className="sm:col-span-2" data-id="element-139" data-mp="1vpmntejqdiq0">
          <label className={labelClass} htmlFor="lead-timing" data-id="element-140" data-mp="1vqo0fngead9u">
            Cuándo quieres hacer la instalación
          </label>
          <select id="lead-timing" name="timing" className={fieldClass} defaultValue={TIMINGS[0]} data-id="element-141" data-mp="2ng0vuu52c0tr">
            {TIMINGS.map(t => <option key={t} value={t} data-id="element-142" data-mp="2se2ejdykty4p">
                {t}
              </option>)}
          </select>
        </div>
        <div className="sm:col-span-2" data-id="element-143" data-mp="2engtbilnhc3n">
          <span className={labelClass} data-id="element-144" data-mp="2ip2d7nwlqqnc">Subir fotos (opcional)</span>
          <label htmlFor="lead-photos" className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-ink/20 bg-white px-4 py-3.5 text-[13px] text-stone-600 transition-colors duration-200 ease-premium hover:border-walnut hover:text-ink" data-id="element-145" data-mp="2in7r469lgsho">
            <PaperclipIcon className="h-4 w-4" aria-hidden="true" data-id="element-146" data-mp="zojmpesfwc05" />
            {fileName ?? 'Añade fotos del suelo, rodapiés o puertas'}
          </label>
          <input ref={filesRef} id="lead-photos" name="photos" type="file" accept="image/*" multiple className="sr-only" onChange={e => {
          const files = e.target.files;
          setFileName(files && files.length > 0 ? `${files.length} archivo(s) añadido(s)` : null);
        }} data-id="element-147" data-mp="19gpfgqqimxge" />
        </div>
      </div>

      {status === 'error' && <div className="mt-7 flex gap-4 rounded-2xl border border-walnut/40 bg-white p-5" data-id="element-148" data-mp="lqv2m96758w5">
          <AlertTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-walnut" aria-hidden="true" data-id="element-149" data-mp="lt7p8zmwg7fj" />
          <p className="text-[13px] leading-relaxed text-ink" data-id="element-150" data-mp="226xz6n0p9px3">
            No hemos podido enviar la solicitud en este momento. Vuelve a intentarlo o escríbenos
            directamente por{' '}
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="border-b border-walnut/50 text-walnut" data-id="element-151" data-mp="3qmwfzqf9vmud">
              WhatsApp
            </a>
            .
          </p>
        </div>}

      <button type="submit" disabled={status === 'sending'} className="mt-8 inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-ink px-8 py-4 text-[13px] font-medium tracking-wide text-cream transition-[background-color,box-shadow] duration-200 ease-premium hover:bg-charcoal hover:shadow-lift disabled:opacity-60 sm:w-auto" data-id="element-152" data-mp="99kc40jtrky9">
        {status === 'sending' && <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-153" data-mp="s4r3yt8cmx8o" />}
        {status === 'sending' ? 'Enviando…' : 'Enviar y recibir presupuesto cerrado'}
      </button>
      <p className="mt-4 text-[12px] text-stone-600" data-id="element-154" data-mp="2u0kp9xv7fmao">
        Sin compromiso. Tus datos solo se usan para preparar tu presupuesto.
      </p>
    </form>;
}